import { BRAND } from "@/lib/brand";
import { Check, Quote, Mail, Download, BookOpenCheck, HeartHandshake, Users } from "lucide-react";

const EXCERPTS = [
  {
    chapter: "Healing the man within",
    text: "The way forward begins the moment you stop running from yourself.",
  },
  {
    chapter: "Breaking generational cycles",
    text: "You can't outrun your father's footsteps until you choose to walk your own path.",
  },
  {
    chapter: "Faith, discipline & purpose",
    text: "Discipline isn't punishment. Discipline is the love you owe yourself.",
  },
];

const buildMailto = (subject, body) =>
  `mailto:${BRAND.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const EBOOK_MAILTO = buildMailto(
  "Order: The Way Forward — E-Book ($19.99)",
  `Hi Joel,\n\nI'd like to order the E-Book edition of "The Way Forward — A Man's Transformation Guide to Health, Wealth & Wellness" ($19.99 — instant download).\n\nName:\nEmail:\nPhone:\n\nPlease send me the download link and next steps for the coaching curriculum.\n\nThank you.`
);

const HARDCOVER_MAILTO = buildMailto(
  "Order: The Way Forward — Hardcover ($29.99)",
  `Hi Joel,\n\nI'd like to order the Hardcover edition of "The Way Forward — A Man's Transformation Guide to Health, Wealth & Wellness" ($29.99 — ships Father's Day, June 21, 2026).\n\nName:\nEmail:\nPhone:\nShipping address:\n\nPlease confirm my order and send me next steps for the coaching curriculum.\n\nThank you.`
);

const BUNDLE_MAILTO = buildMailto(
  "Order: The Way Forward — Bundle E-Book + Hardcover ($39.99)",
  `Hi Joel,\n\nI'd like to order the BUNDLE — both editions of "The Way Forward — A Man's Transformation Guide to Health, Wealth & Wellness" ($39.99).\n\n• E-Book — instant download\n• Hardcover — ships Father's Day, June 21, 2026\n\nName:\nEmail:\nPhone:\nShipping address (for hardcover):\n\nPlease confirm my order and send me next steps for the coaching curriculum.\n\nThank you.`
);

const SCHOLARSHIP_MAILTO = buildMailto(
  "Scholarship / Insurance billing inquiry",
  `Hi Joel,\n\nI'm interested in The Way Forward but would like to learn more about scholarship options or insurance billing for the coaching curriculum.\n\nName:\nEmail:\nPhone:\nA little about my situation:\n\nThank you.`
);

const INCLUDED = [
  {
    icon: BookOpenCheck,
    title: "Meet Joel",
    body: "Every order includes a no-cost (or low-cost) signup to meet Joel and start the curriculum together.",
  },
  {
    icon: Users,
    title: "Telehealth or In-Person",
    body: "Complete the curriculum however you're most comfortable — virtual sessions or face-to-face.",
  },
  {
    icon: HeartHandshake,
    title: "Built for Everyone",
    body: "Geared toward men, but women are welcome too. This is transformation work — everyone has a seat at the table.",
  },
];

export default function BookSection() {
  return (
    <section
      data-testid="book-section"
      id="book"
      className="relative px-6 md:px-12 lg:px-20 py-24 md:py-32 border-t border-[color:var(--border-subtle)] bg-[color:var(--navy-900)]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[color:var(--gold-500)]" />
            <span className="eyebrow">Inside the Book</span>
          </div>

          <h2 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl">
            A roadmap for <br />
            <span className="text-[color:var(--gold-500)]">becoming.</span>
          </h2>

          <p className="mt-6 max-w-lg text-lg text-[color:var(--text-secondary)] leading-relaxed">
            {BRAND.bookSubtitle}. Seven chapters built from real failure, real recovery and real
            results — for men, fathers, families and communities.
          </p>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {BRAND.chapters.map((ch, i) => (
              <li key={ch} className="flex items-start gap-3" data-testid={`chapter-${i}`}>
                <Check className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" size={18} />
                <span className="text-[color:var(--text-primary)]">{ch}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 inline-block px-5 py-4 border border-[color:var(--border-gold)] bg-[color:var(--navy-950)]/60">
            <div className="eyebrow">Available</div>
            <div className="font-display uppercase tracking-tight text-2xl text-white">
              {BRAND.bookReleaseLabel}
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <div className="relative overflow-hidden border border-[color:var(--border-gold)] bg-[color:var(--navy-950)] max-w-md mx-auto">
            <img
              src={BRAND.assets.bookCoverLarge}
              alt="The Way Forward — book cover"
              className="w-full h-auto object-contain"
              style={{ maxHeight: "640px" }}
              data-testid="book-cover-image"
            />
          </div>
        </div>
      </div>

      {/* --- Excerpts --- */}
      <div className="mt-24 md:mt-32">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-[color:var(--gold-500)]" />
          <span className="eyebrow">A Taste of the Book</span>
        </div>
        <h3 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl md:text-5xl max-w-3xl">
          Words you&apos;ll come back to.
        </h3>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {EXCERPTS.map((ex, i) => (
            <figure
              key={i}
              data-testid={`book-excerpt-${i}`}
              className="relative bg-[color:var(--navy-950)] border border-[color:var(--border-subtle)] p-7 md:p-8 group hover:border-[color:var(--border-gold)] transition-colors"
            >
              <Quote
                size={28}
                className="text-[color:var(--gold-500)] opacity-80"
                aria-hidden="true"
              />
              <blockquote className="mt-5 font-display uppercase leading-tight tracking-tight text-xl md:text-2xl text-white">
                &ldquo;{ex.text}&rdquo;
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[color:var(--border-subtle)] eyebrow">
                From — {ex.chapter}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {/* --- Two pricing cards --- */}
      <div className="mt-24 md:mt-32" id="order">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-[color:var(--gold-500)]" />
          <span className="eyebrow">Choose Your Edition</span>
        </div>
        <h3 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl md:text-5xl max-w-3xl">
          Order today. <span className="text-[color:var(--gold-500)]">Walk forward tomorrow.</span>
        </h3>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch" data-testid="book-pricing">
          {/* E-BOOK */}
          <div
            data-testid="order-ebook-card"
            className="relative bg-[color:var(--navy-950)] border border-[color:var(--border-subtle)] p-7 md:p-8 flex flex-col hover:border-[color:var(--border-gold)] transition-colors"
          >
            <div className="absolute top-0 right-0 px-3 py-1 bg-[color:var(--gold-500)] text-[color:var(--navy-950)] eyebrow !text-[color:var(--navy-950)]">
              Instant
            </div>
            <Download size={26} className="text-[color:var(--gold-500)]" />
            <div className="mt-4 eyebrow">E-Book Edition</div>
            <div className="mt-2 font-display font-black uppercase text-4xl md:text-5xl leading-none tracking-tight">
              $19.99
            </div>
            <p className="mt-5 text-[color:var(--text-secondary)] leading-relaxed text-sm md:text-base">
              Instant PDF download the moment you order. Read on any device — start the journey tonight.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm flex-grow">
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>Instant digital download</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>All 7 chapters + reflection prompts</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>Signup to meet Joel + curriculum access</span>
              </li>
            </ul>
            <a
              href={EBOOK_MAILTO}
              data-testid="order-ebook-cta"
              className="btn-outline-gold mt-8 inline-flex items-center justify-center gap-3"
            >
              <Mail size={16} /> Order E-Book — $19.99
            </a>
          </div>

          {/* BUNDLE — featured */}
          <div
            data-testid="order-bundle-card"
            className="relative bg-[color:var(--navy-950)] border-2 p-7 md:p-8 flex flex-col md:-mt-4 md:mb-0"
            style={{
              borderColor: "var(--gold-500)",
              boxShadow: "0 0 0 1px rgba(212,175,55,0.25), 0 30px 60px -30px rgba(212,175,55,0.45)",
            }}
          >
            <div
              className="absolute top-0 right-0 px-3 py-1 eyebrow"
              style={{ background: "var(--gold-500)", color: "var(--navy-950)" }}
            >
              Best Value
            </div>
            <BookOpenCheck size={26} className="text-[color:var(--gold-500)]" />
            <div className="mt-4 eyebrow">E-Book + Hardcover Bundle</div>
            <div className="mt-2 font-display font-black uppercase text-4xl md:text-5xl leading-none tracking-tight">
              $39.99
            </div>
            <p className="mt-5 text-[color:var(--text-secondary)] leading-relaxed text-sm md:text-base">
              Read tonight, hold the keepsake on launch day. Both editions, one price — save $10.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm flex-grow">
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span><strong className="text-white">Instant</strong> E-Book download today</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>Premium hardcover, ships {BRAND.bookReleaseLabel}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>Signup to meet Joel + curriculum access</span>
              </li>
            </ul>
            <a
              href={BUNDLE_MAILTO}
              data-testid="order-bundle-cta"
              className="btn-gold mt-8 inline-flex items-center justify-center gap-3"
            >
              <Mail size={16} /> Order Bundle — $39.99
            </a>
          </div>

          {/* HARDCOVER */}
          <div
            data-testid="order-hardcover-card"
            className="relative bg-[color:var(--navy-950)] border border-[color:var(--border-red)] p-7 md:p-8 flex flex-col hover:border-[color:var(--border-gold)] transition-colors"
            style={{ boxShadow: "0 0 0 1px rgba(200,16,46,0.18)" }}
          >
            <div
              className="absolute top-0 right-0 px-3 py-1 eyebrow"
              style={{ background: "var(--red-500)", color: "white" }}
            >
              Signature
            </div>
            <BookOpenCheck size={26} className="text-[color:var(--gold-500)]" />
            <div className="mt-4 eyebrow">Hardcover Edition</div>
            <div className="mt-2 font-display font-black uppercase text-4xl md:text-5xl leading-none tracking-tight">
              $29.99
            </div>
            <p className="mt-5 text-[color:var(--text-secondary)] leading-relaxed text-sm md:text-base">
              The premium signature hardcover — ships Father&apos;s Day 2026. Built to live on your nightstand.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm flex-grow">
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>Premium hardcover, ships {BRAND.bookReleaseLabel}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>All 7 chapters + reflection prompts</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={16} className="text-[color:var(--gold-500)] mt-1 flex-shrink-0" />
                <span>Signup to meet Joel + curriculum access</span>
              </li>
            </ul>
            <a
              href={HARDCOVER_MAILTO}
              data-testid="order-hardcover-cta"
              className="btn-outline-gold mt-8 inline-flex items-center justify-center gap-3"
            >
              <Mail size={16} /> Order Hardcover — $29.99
            </a>
          </div>
        </div>

        {/* --- "What's included with every order" --- */}
        <div className="mt-14 md:mt-16">
          <div className="text-center mb-10">
            <div className="eyebrow mb-3">Every order includes</div>
            <h4 className="font-display font-black uppercase leading-tight tracking-tight text-3xl md:text-4xl max-w-3xl mx-auto">
              The book is just the <span className="text-[color:var(--gold-500)]">beginning</span>.
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {INCLUDED.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                data-testid={`included-${i}`}
                className="bg-[color:var(--navy-950)] border border-[color:var(--border-subtle)] p-7 md:p-8 hover:border-[color:var(--border-gold)] transition-colors"
              >
                <Icon size={26} className="text-[color:var(--gold-500)]" />
                <div className="mt-4 font-display uppercase text-xl tracking-tight">{title}</div>
                <p className="mt-2 text-sm text-[color:var(--text-secondary)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* --- Access for Everyone (no one turned away) --- */}
        <div
          className="mt-14 md:mt-16 relative overflow-hidden p-8 md:p-12"
          style={{
            background:
              "linear-gradient(135deg, rgba(200,16,46,0.18) 0%, rgba(212,175,55,0.12) 60%, rgba(0,0,0,0) 100%), var(--navy-950)",
            border: "1px solid var(--border-red)",
          }}
          data-testid="access-for-all"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="eyebrow mb-3" style={{ color: "var(--red-500)" }}>
                No one turned away
              </div>
              <h4 className="font-display font-black uppercase leading-tight tracking-tight text-3xl md:text-4xl">
                Can&apos;t pay? <span className="text-[color:var(--gold-500)]">You still belong here.</span>
              </h4>
              <p className="mt-4 max-w-2xl text-[color:var(--text-secondary)] leading-relaxed">
                We work with insurance companies for coaching billing, and scholarships are
                available for anyone who needs them. Money is never the reason someone misses
                their way forward — reach out and we&apos;ll find a path that works for you.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <a
                href={SCHOLARSHIP_MAILTO}
                data-testid="scholarship-cta"
                className="btn-outline-gold inline-flex items-center justify-center gap-3"
              >
                <Mail size={16} /> Ask About Scholarships
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
